import { Box, Button, Stack, Tooltip } from '@mui/material';
import {
    DocumentStatus,
    DocumentType,
    OrderType,
    PurchaseOrderTransition,
    getOrderTypeTranslationId,
    getReceiptAlertColorCode,
} from '@synergy/models';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { MessageIcon } from '@synergy/theme';
import {
    PaloAgeBadge,
    PaloAmount,
    PaloColumn,
    PaloListView,
    PaloRowAction,
    PaloStatusPill,
    usePaloListSort,
} from '@synergy/components-palo';
import { ReactElement, useEffect, useState } from 'react';
import { UrlFilter } from '@synergy/components';
import { changePurchaseOrderToCredit, getPurchaseOrderStatusTranslationId } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import * as PurchaseOrdersActions from '../purchase-order-actions';
import { ContactPresenter } from '../../../common/components/contact-presenter/contact-presenter';
import { ConverterReceiptModal } from '../../convert-receipt/converter-receipt-modal';
import { PaloContactName } from '../../../common/components/palo/palo-contact-card';
import { PaloNamedFilterChips } from '../../../common/components/palo/palo-named-filter-chips';
import { PaloPurchaseOrderOverviewFilter } from './palo-purchase-order-overview-filter';
import { PurchaseOrder } from '../models/purchaseOrder';
import { Transition } from '../../convert-receipt/models/type-transition';
import { TransitionTarget } from '../../convert-receipt/models/internal/transition-target';
import { fetchTransitions, resetTransitions } from '../../../common/actions/transitions-activity-actions';
import { lockedPerson } from '../../../common/utils/locked-partner-notice';
import {
    transitionPurchaseOrderIntoCredit,
    transitionPurchaseOrderIntoInvoice,
} from '../../convert-receipt/transition-actions';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { usePaloColumnFilters } from '../../../common/components/palo/use-palo-column-filters';
import { usePaloListFilters } from '../../../common/components/palo/use-palo-list-filters';
import { usePaloListViews } from '../../../common/components/palo/use-palo-list-views';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

/** A draft this old is pointed out in the list (#2260). */
const DRAFT_AGE_WARN_DAYS = 30;

const netTotalOf = (order: PurchaseOrder): number =>
    order.status === DocumentStatus.Canceled ? -Math.abs(order.totalNetPrice) : order.totalNetPrice;

export const PaloPurchaseOrderOverview = (): ReactElement => {
    const { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns, savedFiltersMenu } =
        usePaloListViews('purchase-orders');
    const columnFilter = usePaloColumnFilters('purchase-orders');
    // Handed to the chips so that an amount reads as an amount.
    const filterFields = usePaloListFilters('purchase-orders');
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { sort, params } = usePaloListSort('purchase-orders');
    const intl = useIntl();
    const { flat: paginated, requesting, entity: purchaseOrder } = useAppSelector(state => state.purchaseOrders);
    const currency = useAppSelector(state => state.settings.currency);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);

    useEffect(() => {
        dispatch(PurchaseOrdersActions.fetchPurchaseOrders(params));
    }, [dispatch, params]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const openDetail = (order: PurchaseOrder): void => navigate(location.pathname + '/' + order.id);

    const headerActions = (
        <Button variant="contained" color="primary" onClick={() => navigate(location.pathname + '/new')}>
            <FormattedMessage id="app.purchaseOrders.new" />
        </Button>
    );

    const [isConverterVisible, setConverterVisible] = useState(false);

    const handleConvertOpen = (id: number): void => {
        dispatch(PurchaseOrdersActions.resetPurchaseOrderState());
        dispatch(resetTransitions());
        dispatch(fetchTransitions(DocumentType.PurchaseOrder, id));
        dispatch(PurchaseOrdersActions.fetchPurchaseOrder(id));
        setConverterVisible(true);
    };

    const handleConvertClose = (): void => {
        dispatch(PurchaseOrdersActions.requestEnd());
        dispatch(PurchaseOrdersActions.resetPurchaseOrderState());
        setConverterVisible(false);
    };

    const handleTransition = (transitionObject: Transition, target: TransitionTarget) =>
        target === TransitionTarget.Invoice
            ? dispatch(transitionPurchaseOrderIntoInvoice(transitionObject as PurchaseOrderTransition))
            : dispatch(
                  transitionPurchaseOrderIntoCredit(
                      changePurchaseOrderToCredit(transitionObject as PurchaseOrderTransition)
                  )
              );

    const columns: PaloColumn<PurchaseOrder>[] = [
        {
            key: 'number',
            type: 'documentNumber',
            sortKey: 'number',
            head: <FormattedMessage id="app.table.column.number" />,
            cell: order => (
                <Stack direction="row" alignItems="center" gap={1}>
                    <Box>{order.number}</Box>
                    {order.receiptAlert && (
                        <Tooltip
                            placement="top-start"
                            title={<Box sx={{ textAlign: 'center' }}>{order.receiptAlert.text}</Box>}
                        >
                            <Stack direction="row" alignItems="center">
                                <MessageIcon
                                    color={getReceiptAlertColorCode(order.receiptAlert.type)}
                                    sx={{ fontSize: 18 }}
                                />
                            </Stack>
                        </Tooltip>
                    )}
                </Stack>
            ),
        },
        {
            key: 'status',
            type: 'status',
            sortKey: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            // The statuses the backend accepts for this list, in the order the
            // document runs through them. "Beendet mit/ohne Folgebeleg" are two
            // entries because that difference is what the list is read for.
            // The status is chosen in the navigation on the left and nowhere else: the
            // same filter in two places wrote two different keys into the address and
            // answered each other (LST-S9, GF 13.10.2026). The active one stands as a
            // chip over the list, where it can be taken off again.
            cell: order => (
                <PaloStatusPill
                    status={order.status}
                    label={<FormattedMessage id={getPurchaseOrderStatusTranslationId(order.status)} />}
                />
            ),
        },
        {
            key: 'type',
            optional: true,
            sortKey: 'type',
            head: <FormattedMessage id="app.table.column.type" />,
            quickFilter: {
                key: 'type',
                options: [OrderType.Manual, OrderType.Shop, OrderType.Anonymous, OrderType.SingleInquiry].map(type => ({
                    value: type,
                    label: <FormattedMessage id={getOrderTypeTranslationId(type)} />,
                })),
            },
            cell: order => <FormattedMessage id={getOrderTypeTranslationId(order.type)} />,
        },
        {
            key: 'customer',
            type: 'name',
            sortKey: 'customerAccount',
            head: <FormattedMessage id="app.table.column.customer" />,
            cell: order => <PaloContactName company={order.customerAccount} kind="customer" />,
        },
        {
            key: 'customerContact',
            type: 'name',
            optional: true,
            sortKey: 'customerContact',
            head: <FormattedMessage id="app.table.column.customerContact" />,
            cell: order => (
                <ContactPresenter
                    person={order.customerContact}
                    fullName={order.customerContact?.fullName}
                    isDeleted={order.customerContact?.isDeleted}
                    isLocked={order.customerContact?.isLocked}
                />
            ),
        },
        {
            key: 'supplier',
            type: 'name',
            sortKey: 'supplierAccount',
            head: <FormattedMessage id="app.table.column.supplier" />,
            cell: order => <PaloContactName company={order.supplierAccount} kind="supplier" />,
        },
        {
            key: 'responsibleContact',
            type: 'name',
            optional: true,
            sortKey: 'responsibleContact',
            head: <FormattedMessage id="app.common.responsibleContact" />,
            cell: order => (
                <ContactPresenter
                    person={order.responsibleContact}
                    fullName={order.responsibleContact?.fullName}
                    isDeleted={order.responsibleContact?.isDeleted}
                    isLocked={order.responsibleContact?.isLocked}
                />
            ),
        },
        {
            key: 'createdAt',
            type: 'date',
            // Room for a draft's age beside the date (#2260).
            width: '200px',
            sortKey: 'createdAt',
            head: <FormattedMessage id="app.table.column.createdAt" />,
            cell: order => (
                <Stack direction="row" alignItems="center" gap={1}>
                    <FormattedDate value={order.createdAt} year="numeric" month="short" day="2-digit" />
                    {DocumentStatus.Created === order.status && (
                        <PaloAgeBadge
                            since={order.createdAt}
                            warnFrom={DRAFT_AGE_WARN_DAYS}
                            messageId="app.age.draftDays"
                        />
                    )}
                </Stack>
            ),
        },
        {
            key: 'total',
            type: 'amount',
            sortKey: 'totalNetPrice',
            currency,
            head: <FormattedMessage id="app.table.column.purchaseOrderTotal" />,
            cell: order => {
                const netTotal = netTotalOf(order);

                return (
                    <Box component="span" sx={{ color: netTotal < 0 ? tokens.color.crit : 'inherit' }}>
                        <PaloAmount value={netTotal} currency={order.currency} base={currency} />
                    </Box>
                );
            },
        },
    ];

    const rowActions = (order: PurchaseOrder): PaloRowAction[] => [
        { label: 'Öffnen', onClick: () => openDetail(order) },
        { label: 'Umwandeln', onClick: () => handleConvertOpen(order.id) },
        {
            label: 'Duplizieren',
            onClick: () =>
                dispatch(
                    PurchaseOrdersActions.duplicatePurchaseOrder(order.id, [
                        order.customerAccount,
                        order.supplierAccount,
                        lockedPerson(order.customerContact),
                        lockedPerson(order.supplierContact),
                    ])
                ),
        },
        {
            label: 'Stornieren',
            onClick: () => dispatch(PurchaseOrdersActions.cancelPurchaseOrder(order.id)),
        },
        { label: 'Löschen', onClick: () => dispatch(PurchaseOrdersActions.deletePurchaseOrder(order.id)) },
    ];

    const handleExport = (): void => {
        const filename = `${intl.formatMessage({ id: 'app.export.purchaseOrders' })}.csv`;
        dispatch(PurchaseOrdersActions.exportPurchaseOrders(filename, searchParams));
    };

    return (
        <UrlFilter useLegacyFilter={true}>
            <PaloListView<PurchaseOrder>
                title={<FormattedMessage id="app.navigation.purchaseOrders" />}
                pageTitle={intl.formatMessage({ id: 'app.navigation.purchaseOrders' })}
                onExport={handleExport}
                filters={
                    <>
                        <PaloPurchaseOrderOverviewFilter />
                        {savedFiltersMenu}
                    </>
                }
                filterChips={
                    <PaloNamedFilterChips listKey="purchase-orders" fields={filterFields} currency={currency} />
                }
                sort={sort}
                onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                searchValue={searchParams.get('search') ?? ''}
                searchPlaceholder="Nummer, Lieferant, Kunde, Artikel …"
                onSearchChange={value => {
                    const next = new URLSearchParams(searchParams);
                    if (value) {
                        next.set('search', value);
                    } else {
                        next.delete('search');
                    }
                    next.set('page', '1');
                    setSearchParams(next, { replace: true });
                }}
                headerActions={headerActions}
                columns={orderColumns(columns)}
                columnFilter={columnFilter}
                onMoveColumn={moveColumn}
                hiddenColumnKeys={hiddenColumnKeys}
                shownColumnKeys={shownColumnKeys}
                onToggleColumn={toggleColumn}
                items={paginated.items}
                getRowId={order => order.id}
                requesting={requesting}
                emptyLabel="Keine Bestellungen."
                onRowClick={openDetail}
                rowActions={rowActions}
                pagination={{
                    count: paginated.total,
                    page,
                    limit,
                    onPageChange: newPage => updateParams({ page: String(newPage) }),
                    onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
                }}
            >
                {purchaseOrder && (
                    <ConverterReceiptModal
                        isVisible={isConverterVisible}
                        source={purchaseOrder}
                        onClose={handleConvertClose}
                        onConvert={handleTransition}
                    />
                )}
            </PaloListView>
        </UrlFilter>
    );
};
